import { openDB, type DBSchema } from 'idb'

export type OfflineMutationType = 'activate_tree' | 'submit_observation' | 'complete_care_task'

export interface OfflineAttachment {
  blob: Blob
  fileName: string
  contentType: string
  purpose: string
}

export interface OfflineMutation {
  id: string
  type: OfflineMutationType
  payload: unknown
  attachment?: OfflineAttachment
  createdAt: string
  attempts: number
  lastError?: string
}

interface TreePassportDB extends DBSchema {
  outbox: {
    key: string
    value: OfflineMutation
    indexes: { 'by-created-at': string }
  }
}

const dbPromise = typeof window === 'undefined'
  ? null
  : openDB<TreePassportDB>('tree-passport-field', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('outbox')) {
          const outbox = db.createObjectStore('outbox', { keyPath: 'id' })
          outbox.createIndex('by-created-at', 'createdAt')
        }
      },
    })

export function mutationId() {
  return `mut_${Date.now().toString(36)}_${crypto.randomUUID().slice(0, 8)}`
}

export function attachmentFromFile(file: File, purpose: string): OfflineAttachment {
  return {
    blob: file,
    fileName: file.name || 'tree-photo.jpg',
    contentType: file.type || 'image/jpeg',
    purpose,
  }
}

export async function queueMutation(
  type: OfflineMutationType,
  payload: unknown,
  id = mutationId(),
  attachment?: OfflineAttachment,
) {
  if (!dbPromise) return id
  const db = await dbPromise
  await db.put('outbox', {
    id,
    type,
    payload,
    attachment,
    createdAt: new Date().toISOString(),
    attempts: 0,
  })
  window.dispatchEvent(new CustomEvent('tree-passport:outbox-changed'))
  return id
}

export async function listMutations() {
  if (!dbPromise) return [] as OfflineMutation[]
  const db = await dbPromise
  return db.getAllFromIndex('outbox', 'by-created-at')
}

export async function outboxCount() {
  if (!dbPromise) return 0
  const db = await dbPromise
  return db.count('outbox')
}

async function uploadQueuedAttachment(item: OfflineMutation, mutationResponse: any) {
  if (!item.attachment) return

  const owner = item.type === 'activate_tree'
    ? { observationId: mutationResponse.observationId, treeId: mutationResponse.treeId }
    : item.type === 'submit_observation'
      ? { observationId: mutationResponse.observationId }
      : {}

  if (!('observationId' in owner) || !owner.observationId) {
    throw new Error('Mutation synced but media owner was not returned')
  }

  const ticketResponse = await fetch('/api/media/ticket', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      fileName: item.attachment.fileName,
      contentType: item.attachment.contentType,
      byteSize: item.attachment.blob.size,
      purpose: item.attachment.purpose,
      ...owner,
    }),
  })
  if (!ticketResponse.ok) throw new Error(`Media ticket HTTP ${ticketResponse.status}`)
  const ticket = await ticketResponse.json() as { uploadUrl: string }

  const uploadResponse = await fetch(ticket.uploadUrl, {
    method: 'PUT',
    headers: { 'content-type': item.attachment.contentType },
    body: item.attachment.blob,
  })
  if (!uploadResponse.ok) throw new Error(`Media upload HTTP ${uploadResponse.status}`)
}

export async function syncOutbox() {
  if (!dbPromise || !navigator.onLine) return { synced: 0, failed: 0 }
  const db = await dbPromise
  const items = await db.getAllFromIndex('outbox', 'by-created-at')
  let synced = 0
  let failed = 0

  for (const item of items) {
    try {
      const endpoint = item.type === 'activate_tree'
        ? '/api/field/activate'
        : item.type === 'submit_observation'
          ? '/api/observations'
          : '/api/care/complete'

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'idempotency-key': item.id,
        },
        body: JSON.stringify(item.payload),
      })

      const responseBody = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${(responseBody as any)?.error || 'sync_failed'}`)

      await uploadQueuedAttachment(item, responseBody)
      await db.delete('outbox', item.id)
      synced += 1
    } catch (error) {
      failed += 1
      await db.put('outbox', {
        ...item,
        attempts: item.attempts + 1,
        lastError: error instanceof Error ? error.message : 'Unknown sync error',
      })
    }
  }

  window.dispatchEvent(new CustomEvent('tree-passport:outbox-changed'))
  return { synced, failed }
}

export function registerConnectivitySync() {
  if (typeof window === 'undefined') return () => undefined
  const onOnline = () => void syncOutbox()
  window.addEventListener('online', onOnline)
  return () => window.removeEventListener('online', onOnline)
}
